/**
 * 사용자가 요청한 AI 요약 자동 채우기 관리
 * - 사용자가 요청한 AI 요약만 완료 시 자동으로 회의록에 채워짐
 * - localStorage에 요청한 pipelineId 목록 저장
 */

const STORAGE_PREFIX = 'pending-ai-fill-';

const getStorageKey = (documentId: string) => `${STORAGE_PREFIX}${documentId}`;

/**
 * 사용자가 요청한 AI 요약 ID 목록 조회
 */
export const getPendingAiSummaries = (documentId: string): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const key = getStorageKey(documentId);
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

/**
 * AI 요약 요청 시 ID 추가
 */
export const addPendingAiSummary = (
  documentId: string,
  pipelineId: string,
) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getStorageKey(documentId);
    const list = getPendingAiSummaries(documentId);

    // 중복 방지
    if (!list.includes(pipelineId)) {
      list.push(pipelineId);
      window.localStorage.setItem(key, JSON.stringify(list));
    }
  } catch (e) {
    console.error('Failed to add pending AI summary:', e);
  }
};

/**
 * AI 요약이 완료되어 채워진 후 ID 제거
 */
export const removePendingAiSummary = (
  documentId: string,
  pipelineId: string,
) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getStorageKey(documentId);
    const list = getPendingAiSummaries(documentId);
    const filtered = list.filter((id) => id !== pipelineId);

    if (filtered.length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(filtered));
    }
  } catch (e) {
    console.error('Failed to remove pending AI summary:', e);
  }
};

/**
 * 특정 pipelineId가 사용자가 요청한 것인지 확인
 */
export const isPendingAiSummary = (
  documentId: string,
  pipelineId: string,
): boolean => {
  const list = getPendingAiSummaries(documentId);
  return list.includes(pipelineId);
};

/**
 * 특정 문서의 모든 pending AI 요약 제거 (문서 삭제 시 등)
 */
export const clearPendingAiSummaries = (documentId: string) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getStorageKey(documentId);
    window.localStorage.removeItem(key);
  } catch (e) {
    console.error('Failed to clear pending AI summaries:', e);
  }
};

/**
 * 모든 문서의 pending AI 요약 제거 (로그아웃 시)
 */
export const clearAllPendingAiSummaries = () => {
  if (typeof window === 'undefined') return;
  try {
    // 모든 localStorage 키를 순회하며 pending-ai-fill- 로 시작하는 키 삭제
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    console.error('Failed to clear all pending AI summaries:', e);
  }
};
